// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Importér BIM-model (frontend-ayw.4 Speckle, frontend-ayw.9 IFC; ruxd PR
 * #12): a Revit model from Speckle with the user's own Speckle token, or an
 * uploaded IFC file, imported into the case as a job. The dialog submits,
 * then follows the job through `useJobs()` (progress, the outputs summary,
 * the failure) — closing it leaves the job running and the job toaster
 * following it. The Ground re-reads the imported model when it finishes.
 * Imports are case writes: the dialog renders nothing to a viewer.
 */

import { useEffect, useId, useRef, useState, type ReactNode, type RefObject } from 'react';

import { api, type IfcUploadHandle } from '../../api/client';
import type { Job, SpeckleTokenStatus } from '../../api/types';
import { isTerminal } from '../../api/types';
import { KONTO_PATH, formatBytes } from '../../app/cases';
import { markOwnImport } from '../../app/bygning/ownImports';
import { useCanEdit } from '../../app/CaseRoleContext';
import { useJobs } from '../../app/JobsContext';
import { useAsync } from '../../app/useAsync';
import {
  DEFAULT_OPTIONS_DRAFT,
  IFC_HEAD_BYTES,
  IMPORT_STAGE_LABEL,
  ifcFileCheck,
  importErrorText,
  importJobErrorText,
  importOptionsCheck,
  importSummary,
  isImportStage,
  looksLikeSpeckleLink,
  parseSpeckleLink,
  sendIfcChunks,
  speckleImportCheck,
  speckleTokenUsable,
  uploadNoticeText,
  uploadProgressText,
  type ImportOptionsDraft,
  type ImportStep,
  type UploadNotice,
} from '../../bygning/bimImport';
import { speckleErrorText, speckleHost, speckleStatusText } from '../../konto/speckle';
import { Button } from '../Button';
import { Checkbox } from '../Checkbox';
import { SegmentedControl } from '../SegmentedControl';
import { Spinner } from '../Spinner';
import { StageProgress } from '../StageProgress';
import { TextInput } from '../TextInput';
import { TextLink } from '../TextLink';
import { FormDialog } from '../kortlaegning/FormDialog';
import formStyles from '../kortlaegning/FormDialog.module.css';
import styles from './BimImportDialog.module.css';

export type BimImportTab = 'speckle' | 'ifc';

export interface BimImportDialogProps {
  onClose: () => void;
  /** The tab it opens on (default Speckle). */
  initialTab?: BimImportTab;
  /**
   * A panel of Rediger › Import (frontend-u0o.1) rather than a modal: the
   * source is picked in the page's source list (no Speckle/IFC tabs here),
   * there is nothing to cancel on the form, and a finished import offers
   * "Ny import" instead of "Luk". `onClose` is then unused.
   */
  inline?: boolean;
}

/** Where the dialog is: filling the form, sending the file, starting the job, or following it. */
type Phase =
  | { kind: 'form' }
  | { kind: 'uploading'; sent: number; total: number; notice: UploadNotice | null }
  | { kind: 'starting' }
  | { kind: 'job'; job: Job };

/** A picked IFC file and what the client checks made of it. */
interface PickedFile {
  file: File;
  problem: string | null;
}

/** ruxd's limit on an upload's `name`. */
const MAX_NAME = 200;

const TABS = [
  { value: 'speckle' as const, label: 'Speckle (Revit)' },
  { value: 'ifc' as const, label: 'IFC-fil' },
];

export function BimImportDialog({ onClose, initialTab = 'speckle', inline = false }: BimImportDialogProps) {
  const canEdit = useCanEdit();
  const { jobs } = useJobs();
  const ids = { tab: useId(), panel: useId(), file: useId() };

  const [tab, setTab] = useState<BimImportTab>(initialTab);
  const [phase, setPhase] = useState<Phase>({ kind: 'form' });
  const [error, setError] = useState<string | null>(null);
  const [options, setOptions] = useState<ImportOptionsDraft>(DEFAULT_OPTIONS_DRAFT);

  // Speckle tab.
  const tokenRead = useAsync<SpeckleTokenStatus>((signal) => api.speckleToken(signal), []);
  const token = tokenRead.data ?? null;
  const [link, setLink] = useState('');
  const [linkNote, setLinkNote] = useState<{ ok: boolean; text: string } | null>(null);
  const [linkServer, setLinkServer] = useState<string | null>(null);
  const [project, setProject] = useState('');
  const [model, setModel] = useState('');
  const [version, setVersion] = useState('');

  // IFC tab.
  const [picked, setPicked] = useState<PickedFile | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  // The request in flight: an upload (with its case-pinned handle once
  // begun), or the submit of the job. `completeSent`: from then on the
  // upload is the server's (it becomes the job's file, or is deleted by a
  // 422) — never DELETE it.
  const inflight = useRef<{ controller: AbortController; handle: IfcUploadHandle | null; completeSent: boolean } | null>(null);

  // An upload still running when the dialog goes away is abandoned on the server too.
  useEffect(
    () => () => {
      const f = inflight.current;
      if (!f) return;
      f.controller.abort();
      if (f.handle && !f.completeSent) void f.handle.abort().catch(() => undefined);
    },
    [],
  );

  // The submitted job as the events describe it (the 202 answer until the first event).
  const followed = phase.kind === 'job' ? (jobs.find((j) => j.id === phase.job.id) ?? phase.job) : null;

  if (!canEdit) return null;

  function clearIds() {
    setLinkServer(null);
    setProject('');
    setModel('');
    setVersion('');
  }

  function onLinkChange(text: string) {
    setLink(text);
    setError(null);
    if (text.trim() === '') {
      setLinkNote(null);
      clearIds();
      return;
    }
    if (!looksLikeSpeckleLink(text)) {
      // Not a link (yet): nothing from an earlier link may stay behind.
      setLinkNote({ ok: false, text: 'Indsæt hele linket, der begynder med https://.' });
      clearIds();
      return;
    }
    const parsed = parseSpeckleLink(text);
    if (!parsed.ok) {
      // A link that cannot be read must not leave an earlier link's ids behind.
      setLinkNote({ ok: false, text: parsed.reason });
      clearIds();
      return;
    }
    const ref = parsed.value;
    setProject(ref.project);
    setModel(ref.model);
    setVersion(ref.version ?? '');
    setLinkServer(ref.server);
    setLinkNote({
      ok: true,
      text: `${ref.server ? `${speckleHost(ref.server)} · ` : ''}model ${ref.model}${ref.version ? ` · version ${ref.version}` : ' · seneste version'}`,
    });
  }

  async function pickFile(file: File | undefined) {
    setError(null);
    if (!file) return;
    let head = new Uint8Array();
    try {
      head = new Uint8Array(await file.slice(0, IFC_HEAD_BYTES).arrayBuffer());
    } catch {
      setPicked({ file, problem: 'Filen kunne ikke læses.' });
      return;
    }
    const check = ifcFileCheck(file, head);
    setPicked({ file, problem: check.ok ? null : check.reason });
  }

  /** The job was accepted: follow it, and tell the Ground it is this page's own import. */
  function accepted(job: Job) {
    inflight.current = null;
    markOwnImport(job.id);
    setPhase({ kind: 'job', job });
  }

  async function submitSpeckle() {
    const check = speckleImportCheck({ linkServer, project, model, version, options }, token);
    if (!check.ok) {
      setError(check.reason);
      return;
    }
    setError(null);
    const controller = new AbortController();
    inflight.current = { controller, handle: null, completeSent: false };
    setPhase({ kind: 'starting' });
    try {
      accepted(await api.importSpeckle(check.value, controller.signal));
    } catch (cause) {
      if (controller.signal.aborted) return; // stopStarting has said so
      inflight.current = null;
      setPhase({ kind: 'form' });
      setError(importErrorText(cause, 'speckle'));
    }
  }

  async function submitIfc() {
    if (!picked) {
      setError('Vælg en IFC-fil først.');
      return;
    }
    if (picked.problem) {
      setError(picked.problem);
      return;
    }
    const opts = importOptionsCheck(options);
    if (!opts.ok) {
      setError(opts.reason);
      return;
    }
    setError(null);
    const file = picked.file;
    const controller = new AbortController();
    const current: { controller: AbortController; handle: IfcUploadHandle | null; completeSent: boolean } = {
      controller,
      handle: null,
      completeSent: false,
    };
    inflight.current = current;
    let step: ImportStep = 'ifc-begin';
    setPhase({ kind: 'uploading', sent: 0, total: file.size, notice: null });
    try {
      // The handle is pinned to this case's URL: every later request of the upload goes there.
      const handle = await api.beginIfcUpload(file.name.slice(0, MAX_NAME), file.size, controller.signal);
      current.handle = handle;
      const session = handle.upload;
      if (file.size > session.max_bytes) {
        throw new RangeError(`Filen fylder ${formatBytes(file.size)} — serveren tager højst ${formatBytes(session.max_bytes)}.`);
      }
      step = 'ifc-chunk';
      await sendIfcChunks(
        file.size,
        session.chunk_bytes,
        session.received,
        {
          put: (start, end) => handle.append(start, file.slice(start, end), controller.signal),
          status: () => handle.status(controller.signal),
          sleep: (ms) => new Promise((done) => setTimeout(done, ms)),
        },
        // A chunk landing (even after a retry or a resync) clears the notice.
        (received) => setPhase({ kind: 'uploading', sent: received, total: file.size, notice: null }),
        controller.signal,
        (notice) => setPhase((p) => (p.kind === 'uploading' ? { ...p, notice } : p)),
      );
      step = 'ifc-complete';
      setPhase({ kind: 'starting' });
      current.completeSent = true;
      accepted(await handle.complete(opts.value, controller.signal));
    } catch (cause) {
      if (controller.signal.aborted) return; // cancelUpload / stopStarting has said so
      inflight.current = null;
      // Best effort: the staged bytes are no use now — unless complete was
      // sent, after which the file is the server's (a 422 deleted it).
      if (current.handle && !current.completeSent) void current.handle.abort().catch(() => undefined);
      setPhase({ kind: 'form' });
      setError(cause instanceof RangeError ? cause.message : importErrorText(cause, step));
    }
  }

  function cancelUpload() {
    const f = inflight.current;
    inflight.current = null;
    if (f) {
      f.controller.abort();
      if (f.handle && !f.completeSent) void f.handle.abort().catch(() => undefined);
    }
    setPhase({ kind: 'form' });
    setError('Uploaden blev annulleret. Intet blev importeret.');
  }

  /** Stop waiting for the job's 202. The request may already have reached the server. */
  function stopStarting() {
    const f = inflight.current;
    inflight.current = null;
    f?.controller.abort();
    setPhase({ kind: 'form' });
    setError(
      'Afbrudt. Serveren kan nå at have startet importen alligevel — følg den i jobvisningen nederst til højre, før du prøver igen.',
    );
  }

  function again() {
    setPhase({ kind: 'form' });
    setError(null);
  }

  const submit = () => {
    if (phase.kind !== 'form') return;
    void (tab === 'speckle' ? submitSpeckle() : submitIfc());
  };

  // ---------------------------------------------------------------- view --

  const tokenOk = speckleTokenUsable(token);
  const canSubmit =
    phase.kind === 'form' && (tab === 'speckle' ? tokenOk && project.trim() !== '' && model.trim() !== '' : picked !== null && picked.problem === null);

  let body: ReactNode;
  let actions: ReactNode;
  if (phase.kind === 'form') {
    body = (
      <>
        {!inline && (
          <SegmentedControl
            aria-label="Kilde"
            role="tablist"
            items={TABS.map((t) => ({ ...t, id: `${ids.tab}-${t.value}`, controls: ids.panel }))}
            value={tab}
            onChange={(value) => {
              setTab(value);
              setError(null);
            }}
            className={styles.tabs}
          />
        )}
        <div
          id={ids.panel}
          role={inline ? undefined : 'tabpanel'}
          aria-labelledby={inline ? undefined : `${ids.tab}-${tab}`}
          className={styles.panel}
        >
          {tab === 'speckle' ? (
            <SpeckleFields
              tokenLoading={tokenRead.loading && !token}
              tokenError={tokenRead.error ? speckleErrorText(tokenRead.error, 'load') : null}
              token={token}
              link={link}
              linkNote={linkNote}
              onLink={onLinkChange}
              project={project}
              model={model}
              version={version}
              onProject={(v) => {
                setProject(v);
                setError(null);
              }}
              onModel={(v) => {
                setModel(v);
                setError(null);
              }}
              onVersion={(v) => {
                setVersion(v);
                setError(null);
              }}
            />
          ) : (
            <IfcFields
              inputId={ids.file}
              inputRef={fileRef}
              picked={picked}
              onPick={(f) => void pickFile(f)}
            />
          )}
          <AdvancedOptions draft={options} onChange={setOptions} />
          <p className={styles.replaces}>
            En ny import erstatter de elementer, der tidligere er importeret. Scannede og manuelt oprettede bevares.
          </p>
        </div>
      </>
    );
    actions = (
      <>
        {!inline && (
          <Button variant="outline" size="sm" onClick={onClose}>
            Annuller
          </Button>
        )}
        <Button type="submit" variant="primary" size="sm" disabled={!canSubmit}>
          {tab === 'speckle' ? 'Importér' : 'Upload og importér'}
        </Button>
      </>
    );
  } else if (phase.kind === 'uploading') {
    body = (
      <div className={styles.status}>
        <p className={styles.statusHead}>
          <span className={styles.fileName}>{picked?.file.name}</span>
          <span className={styles.statusMeta}>uploades</span>
        </p>
        <progress className={styles.bar} max={phase.total} value={phase.sent} aria-label="Upload af IFC-fil" />
        <p className={styles.statusMeta}>{uploadProgressText(phase.sent, phase.total)}</p>
        {phase.notice && (
          <p className={styles.notice} role="status" aria-live="polite">
            {uploadNoticeText(phase.notice, phase.sent, phase.total)}
          </p>
        )}
      </div>
    );
    actions = (
      <Button variant="outline" size="sm" tone="danger" onClick={cancelUpload}>
        Annullér upload
      </Button>
    );
  } else if (phase.kind === 'starting') {
    body = (
      <div className={styles.status}>
        <Spinner label={tab === 'ifc' ? 'Tjekker filen og starter importen…' : 'Starter importen…'} />
      </div>
    );
    actions = (
      <Button variant="outline" size="sm" onClick={stopStarting}>
        Annuller
      </Button>
    );
  } else {
    const job = followed ?? phase.job;
    body = <JobStatus job={job} />;
    const done = isTerminal(job.status);
    actions = (
      <>
        {done && job.status !== 'succeeded' && (
          <Button variant="outline" size="sm" onClick={again}>
            Prøv igen
          </Button>
        )}
        {inline ? (
          done && (
            <Button type="button" variant="primary" size="sm" onClick={again}>
              Ny import
            </Button>
          )
        ) : (
          <Button type="button" variant={done ? 'primary' : 'outline'} size="sm" onClick={onClose}>
            Luk
          </Button>
        )}
      </>
    );
  }

  return (
    <FormDialog
      title={inline ? (tab === 'speckle' ? 'Speckle (Revit)' : 'IFC-fil') : 'Importér BIM-model'}
      subtitle={
        inline
          ? tab === 'speckle'
            ? 'Hent bygningens etager, vægge, åbninger og rum — og modellen som lag i 3D — fra Revit via Speckle.'
            : 'Hent bygningens etager, vægge, åbninger og rum — og modellen som lag i 3D — fra en IFC-fil.'
          : 'Hent bygningens etager, vægge, åbninger og rum — og modellen som lag i 3D — fra Revit via Speckle eller fra en IFC-fil.'
      }
      onCancel={
        phase.kind === 'uploading' ? () => undefined : phase.kind === 'starting' ? stopStarting : inline ? () => undefined : onClose
      }
      onSubmit={submit}
      error={error}
      actions={actions}
      className={inline ? undefined : styles.dialog}
      noValidate
      inline={inline}
    >
      {body}
    </FormDialog>
  );
}

// -------------------------------------------------------------- speckle --

function SpeckleFields(props: {
  tokenLoading: boolean;
  tokenError: string | null;
  token: SpeckleTokenStatus | null;
  link: string;
  linkNote: { ok: boolean; text: string } | null;
  onLink: (v: string) => void;
  project: string;
  model: string;
  version: string;
  onProject: (v: string) => void;
  onModel: (v: string) => void;
  onVersion: (v: string) => void;
}) {
  const { token } = props;
  const usable = speckleTokenUsable(token);
  return (
    <>
      <TokenLine loading={props.tokenLoading} error={props.tokenError} token={token} />
      <label className={formStyles.field}>
        <span className={formStyles.fieldLabel}>Link til modellen</span>
        <TextInput
          variant="outline"
          size="sm"
          block
          mono
          inputMode="url"
          spellCheck={false}
          autoComplete="off"
          placeholder="https://app.speckle.systems/projects/…/models/…"
          value={props.link}
          onChange={(e) => props.onLink(e.target.value)}
          autoFocus={usable}
        />
        {props.linkNote ? (
          <span className={props.linkNote.ok ? styles.linkOk : styles.linkBad}>{props.linkNote.text}</span>
        ) : (
          <span className={styles.help}>Kopiér linket fra modellens side i Speckle — så udfyldes felterne herunder.</span>
        )}
      </label>
      <div className={styles.ids}>
        <IdField label="Projekt-id" value={props.project} onChange={props.onProject} />
        <IdField label="Model-id" value={props.model} onChange={props.onModel} />
        <IdField label="Version" value={props.version} onChange={props.onVersion} placeholder="seneste" />
      </div>
    </>
  );
}

function IdField({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <label className={formStyles.field}>
      <span className={formStyles.fieldLabel}>{label}</span>
      <TextInput
        variant="outline"
        size="sm"
        block
        mono
        spellCheck={false}
        autoComplete="off"
        maxLength={128}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

/** The user's Speckle connection, which the import fetches with. */
function TokenLine({ loading, error, token }: { loading: boolean; error: string | null; token: SpeckleTokenStatus | null }) {
  if (loading) return <Spinner label="Henter din Speckle-forbindelse…" />;
  if (error || !token)
    return (
      <div className={`${styles.token} ${styles.tokenWarn}`}>
        <p className={styles.tokenHead}>Speckle-forbindelsen kunne ikke hentes</p>
        <p className={styles.tokenText}>{error ?? 'Prøv igen om lidt.'}</p>
      </div>
    );
  if (!token.configured)
    return (
      <div className={`${styles.token} ${styles.tokenWarn}`}>
        <p className={styles.tokenHead}>Intet Speckle-token</p>
        <p className={styles.tokenText}>
          Importen henter modellen med dit eget personlige adgangstoken fra Speckle. Gem et under Konto, og kom så tilbage hertil.
        </p>
        <TextLink size="sm" href={KONTO_PATH}>
          Gå til Konto › Speckle
        </TextLink>
      </div>
    );
  const text = speckleStatusText(token);
  if (text.warning)
    return (
      <div className={`${styles.token} ${styles.tokenWarn}`}>
        <p className={styles.tokenHead}>{text.headline}</p>
        <p className={styles.tokenText}>{text.warning}</p>
        <TextLink size="sm" href={KONTO_PATH}>
          Gå til Konto › Speckle
        </TextLink>
      </div>
    );
  return (
    <div className={styles.token}>
      <span className={styles.tokenDot} aria-hidden="true" />
      <p className={styles.tokenHead}>{text.headline}</p>
      <TextLink size="sm" tone="muted" href={KONTO_PATH}>
        Skift
      </TextLink>
    </div>
  );
}

// ------------------------------------------------------------------ ifc --

function IfcFields({
  inputId,
  inputRef,
  picked,
  onPick,
}: {
  inputId: string;
  inputRef: RefObject<HTMLInputElement | null>;
  picked: PickedFile | null;
  onPick: (file: File | undefined) => void;
}) {
  return (
    <div className={formStyles.field}>
      <span className={formStyles.fieldLabel}>IFC-fil</span>
      <input
        id={inputId}
        ref={inputRef}
        type="file"
        accept=".ifc"
        className={styles.fileInput}
        onChange={(e) => {
          onPick(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
      <div
        className={styles.drop}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          onPick(e.dataTransfer.files[0]);
        }}
      >
        {picked ? (
          <div className={styles.picked}>
            <span className={styles.fileName}>{picked.file.name}</span>
            <span className={styles.statusMeta}>{formatBytes(picked.file.size)}</span>
          </div>
        ) : (
          <span className={styles.help}>Træk en .ifc-fil hertil, eller vælg den. Højst 1 GB.</span>
        )}
        <Button variant="outline" size="sm" onClick={() => inputRef.current?.click()}>
          {picked ? 'Vælg en anden…' : 'Vælg IFC-fil…'}
        </Button>
      </div>
      {picked?.problem && <span className={styles.linkBad}>{picked.problem}</span>}
    </div>
  );
}

// ------------------------------------------------------------- options --

function AdvancedOptions({ draft, onChange }: { draft: ImportOptionsDraft; onChange: (d: ImportOptionsDraft) => void }) {
  return (
    <details className={styles.advanced}>
      <summary className={styles.summary}>Avancerede indstillinger</summary>
      <div className={styles.advancedBody}>
        <div className={styles.ids}>
          <label className={formStyles.field}>
            <span className={formStyles.fieldLabel}>Øverste etages højde</span>
            <TextInput
              variant="outline"
              size="sm"
              block
              mono
              inputMode="decimal"
              suffix="m"
              value={draft.storeyHeight}
              onChange={(e) => onChange({ ...draft, storeyHeight: e.target.value })}
            />
          </label>
          <label className={formStyles.field}>
            <span className={formStyles.fieldLabel}>Mindste vægtykkelse</span>
            <TextInput
              variant="outline"
              size="sm"
              block
              mono
              inputMode="decimal"
              suffix="m"
              value={draft.minWallThickness}
              onChange={(e) => onChange({ ...draft, minWallThickness: e.target.value })}
            />
          </label>
        </div>
        <Checkbox
          label="Gem modellen som lag i 3D (ét mesh pr. kategori)"
          checked={draft.meshes}
          onChange={(e) => onChange({ ...draft, meshes: e.target.checked })}
        />
      </div>
    </details>
  );
}

// ----------------------------------------------------------------- job --

const STATUS_TEXT: Record<Job['status'], string> = {
  queued: 'i kø',
  running: 'kører',
  succeeded: 'færdig',
  failed: 'fejlede',
  cancelled: 'annulleret',
};

function JobStatus({ job }: { job: Job }) {
  const label = isImportStage(job.stage) ? IMPORT_STAGE_LABEL[job.stage] : job.stage;
  return (
    <div className={styles.status} role="status" aria-live="polite">
      <p className={styles.statusHead}>
        <span className={styles.fileName}>{label}</span>
        <span className={`${styles.pill} ${styles[`pill_${job.status}`]}`}>{STATUS_TEXT[job.status]}</span>
      </p>
      {job.status === 'queued' && <p className={styles.statusMeta}>Venter på, at andre kørsler på sagen bliver færdige…</p>}
      {job.status === 'running' &&
        (job.progress ? <StageProgress progress={job.progress} /> : <Spinner label="Importerer…" />)}
      {!isTerminal(job.status) && (
        <p className={styles.help}>Du kan godt lukke vinduet — importen fortsætter, og modellen vises, når den er færdig.</p>
      )}
      {job.status === 'succeeded' && (
        <>
          <p className={styles.summaryLine}>{importSummary(job.result)}</p>
          <p className={styles.help}>Modellen er hentet ind i 3D, og etagerne står i Overblik.</p>
        </>
      )}
      {(job.status === 'failed' || job.status === 'cancelled') && <p className={styles.failLine}>{importJobErrorText(job)}</p>}
    </div>
  );
}
